import React, { useState, useEffect, useRef } from 'react';
import { Settings, Save, Image as ImageIcon, Mail, Phone, MapPin, Facebook, Youtube, Instagram, Send, Upload, Loader2, CheckCircle, X, DollarSign, Bell, Mic, Key, FlaskConical } from 'lucide-react';
import { settingsAPI } from '../services/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SiteSettingsManager = () => {
  const [settings, setSettings] = useState({
    siteName: '',
    logo: '',
    email: '',
    phone: '',
    whatsapp: '',
    address: '',
    facebook: '',
    youtube: '',
    instagram: '',
    telegram: '',
    openai_api_key: '',
    gemini_api_key: '',
    ai_provider: 'openai',
    ai_model: 'gpt-5',
    paypal_client_id: '',
    paypal_secret: '',
    paypal_mode: 'sandbox',
    notification_expiry_enabled: true,
    notification_expiry_days: 5,
    default_admin_trials: 5,
    voice_intro_enabled: true,
    voice_intro_uses_credits: false,
    multi_device_restriction: true,
    carousel_images: []
  });
  
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const MASKED = '••••••••';
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_provider: 'lws',
    smtp_lws_host: '',
    smtp_lws_port: 587,
    smtp_lws_user: '',
    smtp_lws_password: '',
    smtp_lws_from_email: '',
    smtp_lws_from_name: 'TCF Canada',
    smtp_gmail_user: '',
    smtp_gmail_password: '',
    smtp_gmail_from_name: 'TCF Canada'
  });
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpTestEmail, setSmtpTestEmail] = useState('');
  const [smtpTestLoading, setSmtpTestLoading] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const loadedSettings = await settingsAPI.getSiteSettings();
        if (loadedSettings) {
          setSettings({
            siteName: loadedSettings.site_name || loadedSettings.siteName || '',
            logo: loadedSettings.logo || '',
            email: loadedSettings.email || '',
            phone: loadedSettings.phone || '',
            whatsapp: loadedSettings.whatsapp || '',
            address: loadedSettings.address || '',
            facebook: loadedSettings.facebook || '',
            youtube: loadedSettings.youtube || '',
            instagram: loadedSettings.instagram || '',
            telegram: loadedSettings.telegram || '',
            openai_api_key: loadedSettings.openai_api_key || '',
            gemini_api_key: loadedSettings.gemini_api_key || '',
            ai_provider: loadedSettings.ai_provider || 'openai',
            ai_model: loadedSettings.ai_model || 'gpt-5',
            paypal_client_id: loadedSettings.paypal_client_id || '',
            paypal_secret: loadedSettings.paypal_secret || '',
            paypal_mode: loadedSettings.paypal_mode || 'sandbox',
            notification_expiry_enabled: loadedSettings.notification_expiry_enabled !== undefined ? loadedSettings.notification_expiry_enabled : true,
            notification_expiry_days: loadedSettings.notification_expiry_days || 5,
            default_admin_trials: loadedSettings.default_admin_trials ?? 5,
            voice_intro_enabled: loadedSettings.voice_intro_enabled !== undefined ? loadedSettings.voice_intro_enabled : true,
            voice_intro_uses_credits: loadedSettings.voice_intro_uses_credits !== undefined ? loadedSettings.voice_intro_uses_credits : false,
            multi_device_restriction: loadedSettings.multi_device_restriction !== undefined ? loadedSettings.multi_device_restriction : true,
            carousel_images: loadedSettings.carousel_images || ['', '', '']
          });
        }
        // Also check for uploaded logo on server
        loadServerLogo();
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    const loadSmtp = async () => {
      try {
        const data = await settingsAPI.getSmtpSettings();
        if (data) setSmtpSettings(data);
      } catch {}
    };
    loadSettings();
    loadSmtp();
  }, []);

  const loadServerLogo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/upload/logo`);
      if (response.ok) {
        const data = await response.json();
        if (data.exists && data.url) {
          // Update settings with server logo if no logo is set
          if (!settings.logo) {
            setSettings(prev => ({ ...prev, logo: data.url }));
          }
        }
      }
    } catch (error) {
      console.error('Error loading server logo:', error);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingLogo(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/upload/logo`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setSettings(prev => ({ ...prev, logo: data.url }));
        toast.success('Logo uploadé avec succès !');
      } else {
        toast.error(data.detail || 'Erreur lors de l\'upload');
      }

    } catch (error) {
      toast.error('Erreur lors de l\'upload du logo');
      console.error('Upload error:', error);
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleCarouselUpload = async (index, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/uploads/image/`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const newImages = [...(settings.carousel_images || ['', '', ''])];
        newImages[index] = data.url;
        setSettings({ ...settings, carousel_images: newImages });
        toast.success(`Image ${index + 1} téléchargée`);
      }
    } catch (error) {
      toast.error("Erreur lors du téléchargement de l'image");
    }
  };

  const handleRemoveLogo = () => {
    setSettings(prev => ({ ...prev, logo: '' }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const result = await settingsAPI.updateSiteSettings({
        site_name: settings.siteName,
        logo: settings.logo,
        email: settings.email,
        phone: settings.phone,
        whatsapp: settings.whatsapp,
        address: settings.address,
        facebook: settings.facebook,
        youtube: settings.youtube,
        instagram: settings.instagram,
        telegram: settings.telegram,
        openai_api_key: settings.openai_api_key,
        gemini_api_key: settings.gemini_api_key,
        ai_provider: settings.ai_provider,
        ai_model: settings.ai_model,
        paypal_client_id: settings.paypal_client_id,
        paypal_secret: settings.paypal_secret,
        paypal_mode: settings.paypal_mode,
        notification_expiry_enabled: settings.notification_expiry_enabled,
        notification_expiry_days: settings.notification_expiry_days,
        default_admin_trials: settings.default_admin_trials,
        voice_intro_enabled: settings.voice_intro_enabled,
        voice_intro_uses_credits: settings.voice_intro_uses_credits,
        multi_device_restriction: settings.multi_device_restriction,
        carousel_images: settings.carousel_images
      });
      if (result.success) {
        toast.success('Paramètres mis à jour avec succès');
      } else {
        toast.error(result.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSmtpSave = async () => {
    setSmtpLoading(true);
    try {
      const result = await settingsAPI.updateSmtpSettings(smtpSettings);
      if (result.success) toast.success('Configuration SMTP sauvegardée');
      else toast.error(result.message || 'Erreur lors de la sauvegarde');
    } catch { toast.error('Erreur de connexion'); }
    finally { setSmtpLoading(false); }
  };

  const handleSmtpTest = async () => {
    if (!smtpTestEmail.trim()) { toast.error('Entrez un email de test'); return; }
    setSmtpTestLoading(true);
    try {
      const result = await settingsAPI.testSmtp(smtpTestEmail.trim());
      if (result.success) toast.success('Email de test envoyé avec succès !');
      else toast.error(result.message || 'Échec de l\'envoi');
    } catch { toast.error('Erreur de connexion'); }
    finally { setSmtpTestLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Paramètres du site</h2>
        <p className="text-muted-foreground">Configurez les informations globales de votre plateforme</p>
      </div>

      <form onSubmit={handleSave} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Brand Settings */}
        <div className="bg-card p-6 rounded-xl border space-y-4">
          <div className="flex items-center space-x-3 mb-4">
            <Settings className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold">Identité de marque</h3>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Nom du site</label>
            <input
              type="text"
              value={settings.siteName}
              onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
              data-testid="site-name-input"
              className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              placeholder="TCF Canada"
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center space-x-2">
              <ImageIcon className="h-4 w-4" />
              <span>Logo du site</span>
            </label>
            
            {/* Logo Upload Area */}
            <div className="space-y-3">
              {/* Current logo preview */}
              {settings.logo && (
                <div className="relative p-4 bg-secondary/30 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">Logo actuel :</p>
                  <div className="flex items-center justify-between">
                    <img src={settings.logo.startsWith('http') ? settings.logo : `${API_URL || ''}${settings.logo}`} alt="Logo preview" className="h-16 object-contain" />
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="p-2 rounded-lg text-red-500 hover:bg-red-500/10"
                      title="Supprimer le logo"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Upload button */}
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                    isUploadingLogo 
                      ? 'border-gray-300 bg-gray-100 cursor-not-allowed' 
                      : 'border-primary/50 hover:bg-primary/5 hover:border-primary'
                  }`}
                >
                  {isUploadingLogo ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      <span className="text-sm font-medium">Upload en cours...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-5 w-5 text-primary" />
                      <span className="text-sm font-medium">
                        {settings.logo ? 'Changer le logo' : 'Uploader un logo'}
                      </span>
                    </>
                  )}
                </label>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Formats supportés : PNG, JPG, GIF, WEBP, SVG, ICO
              </p>
              
              {/* OR use URL */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-card text-muted-foreground">ou utilisez une URL</span>
                </div>
              </div>
              
              <input
                type="text"
                value={settings.logo}
                onChange={(e) => setSettings({ ...settings, logo: e.target.value })}
                data-testid="logo-input"
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="https://example.com/logo.png"
              />
            </div>
          </div>
        </div>

        {/* Contact Settings */}
        <div className="bg-card p-6 rounded-xl border space-y-4">
          <div className="flex items-center space-x-3 mb-4">
            <Mail className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold">Informations de contact</h3>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center space-x-2">
              <Mail className="h-4 w-4" />
              <span>Email</span>
            </label>
            <input
              type="email"
              value={settings.email}
              onChange={(e) => setSettings({ ...settings, email: e.target.value })}
              data-testid="email-input"
              className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              placeholder="contact@tcfcanada.com"
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center space-x-2">
              <Phone className="h-4 w-4" />
              <span>Téléphone</span>
            </label>
            <input
              type="tel"
              value={settings.phone}
              onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
              data-testid="phone-input"
              className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              placeholder="+23057235683"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center space-x-2">
              <Phone className="h-4 w-4 text-green-500" />
              <span>WhatsApp (avec +)</span>
            </label>
            <input
              type="tel"
              value={settings.whatsapp}
              onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })}
              data-testid="whatsapp-input"
              className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              placeholder="+23057235683"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center space-x-2">
              <MapPin className="h-4 w-4" />
              <span>Adresse (optionnel)</span>
            </label>
            <textarea
              value={settings.address}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              data-testid="address-input"
              className="w-full px-4 py-3 rounded-lg border bg-background text-sm min-h-[80px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              placeholder="Votre adresse..."
            />
          </div>
        </div>

        {/* Social Media Settings */}
        <div className="bg-card p-6 rounded-xl border space-y-4 lg:col-span-2">
          <div className="flex items-center space-x-3 mb-4">
            <Facebook className="h-6 w-6 text-blue-600" />
            <h3 className="text-xl font-semibold">Réseaux sociaux</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Ajoutez les liens de vos réseaux sociaux. Ces liens seront affichés sur le site.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center space-x-2">
                <Facebook className="h-4 w-4 text-blue-600" />
                <span>Facebook</span>
              </label>
              <input
                type="text"
                value={settings.facebook}
                onChange={(e) => setSettings({ ...settings, facebook: e.target.value })}
                data-testid="facebook-input"
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="https://facebook.com/votrepage"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center space-x-2">
                <Youtube className="h-4 w-4 text-red-600" />
                <span>YouTube</span>
              </label>
              <input
                type="text"
                value={settings.youtube}
                onChange={(e) => setSettings({ ...settings, youtube: e.target.value })}
                data-testid="youtube-input"
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="https://youtube.com/@votrechaine"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center space-x-2">
                <Instagram className="h-4 w-4 text-pink-600" />
                <span>Instagram</span>
              </label>
              <input
                type="text"
                value={settings.instagram}
                onChange={(e) => setSettings({ ...settings, instagram: e.target.value })}
                data-testid="instagram-input"
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="https://instagram.com/votrecompte"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center space-x-2">
                <Send className="h-4 w-4 text-blue-500" />
                <span>Telegram</span>
              </label>
              <input
                type="text"
                value={settings.telegram}
                onChange={(e) => setSettings({ ...settings, telegram: e.target.value })}
                data-testid="telegram-input"
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="https://t.me/votregroupe"
              />
            </div>
          </div>
        </div>

        {/* AI Configuration Settings */}
        <div className="bg-card p-6 rounded-xl border space-y-4 lg:col-span-2">
          <div className="flex items-center space-x-3 mb-4">
            <Settings className="h-6 w-6 text-purple-600" />
            <h3 className="text-xl font-semibold">Intelligence Artificielle</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Configurez les clés API pour la correction automatique des épreuves d'expression.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center space-x-2">
                <span>Clé API OpenAI</span>
              </label>
              <input
                type="password"
                value={settings.openai_api_key}
                onChange={(e) => setSettings({ ...settings, openai_api_key: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="sk-..."
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center space-x-2">
                <span>Clé API Gemini (Google)</span>
              </label>
              <input
                type="password"
                value={settings.gemini_api_key}
                onChange={(e) => setSettings({ ...settings, gemini_api_key: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="AIzaSy..."
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium flex items-center space-x-2">
                <span>Fournisseur IA actif</span>
              </label>
              <select
                value={settings.ai_provider}
                onChange={(e) => setSettings({ ...settings, ai_provider: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <option value="openai">OpenAI (ChatGPT)</option>
                <option value="gemini">Google Gemini</option>
              </select>
            </div>

            {settings.ai_provider === 'openai' && (
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium flex items-center space-x-2">
                  <span>Modèle OpenAI</span>
                </label>
                <select
                  value={settings.ai_model}
                  onChange={(e) => setSettings({ ...settings, ai_model: e.target.value })}
                  className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <option value="gpt-5">GPT-5</option>
                  <option value="gpt-5-mini">GPT-5 mini</option>
                  <option value="gpt-5-nano">GPT-5 nano</option>
                  <option value="gpt-4.1">GPT-4.1</option>
                  <option value="gpt-4.1-mini">GPT-4.1 mini</option>
                  <option value="gpt-4.1-nano">GPT-4.1 nano</option>
                  <optgroup label="Versions GPT-3.x">
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                    <option value="gpt-3.5-turbo-0125">GPT-3.5 Turbo 0125</option>
                    <option value="gpt-3.5-turbo-1106">GPT-3.5 Turbo 1106</option>
                    <option value="gpt-3.5-turbo-0613">GPT-3.5 Turbo 0613</option>
                  </optgroup>
                </select>
                <p className="text-xs text-muted-foreground">
                  Les modèles GPT-3.x sont affichés ici pour compatibilité. Si un modèle n'est plus disponible dans votre compte OpenAI, choisissez une version plus récente.
                </p>
              </div>
            )} 
          </div>
        </div>

        {/* PayPal Configuration Settings */}
        <div className="bg-card p-6 rounded-xl border space-y-4 lg:col-span-2">
          <div className="flex items-center space-x-3 mb-4">
            <DollarSign className="h-6 w-6 text-blue-500" />
            <h3 className="text-xl font-semibold">Configuration PayPal Checkout</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Configurez vos identifiants PayPal pour permettre l'achat automatique de packs d'essais par les centres.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">PayPal Client ID</label>
              <input
                type="text"
                value={settings.paypal_client_id}
                onChange={(e) => setSettings({ ...settings, paypal_client_id: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="Client ID..."
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">PayPal Client Secret</label>
              <input
                type="password"
                value={settings.paypal_secret}
                onChange={(e) => setSettings({ ...settings, paypal_secret: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="Secret..."
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium">Mode de paiement</label>
              <select
                value={settings.paypal_mode}
                onChange={(e) => setSettings({ ...settings, paypal_mode: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <option value="sandbox">Sandbox (Test)</option>
                <option value="live">Live (Production)</option>
              </select>
            </div>
          </div>
        </div>


        {/* Carousel Settings */}
        <div className="bg-card p-6 rounded-xl border space-y-4 lg:col-span-2">
          <div className="flex items-center space-x-3 mb-4">
            <ImageIcon className="h-6 w-6 text-green-500" />
            <h3 className="text-xl font-semibold">Carrousel de la Page d'Accueil</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Personnalisez les 3 images qui défilent sur la page d'accueil. Taille recommandée : 1920x1080px.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((idx) => (
              <div key={idx} className="space-y-4">
                <p className="text-sm font-medium">Image {idx + 1}</p>
                <div className="relative group aspect-video rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-secondary/50">
                  {settings.carousel_images?.[idx] ? (
                    <img 
                      src={settings.carousel_images[idx].startsWith('http') ? settings.carousel_images[idx] : `${API_URL}${settings.carousel_images[idx]}`} 
                      alt={`Slide ${idx + 1}`} 
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="text-center p-4">
                      <ImageIcon className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground">Aucune image</p>
                    </div>
                  )}
                  
                  <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                    <div className="flex flex-col items-center text-white space-y-2">
                      <Upload className="h-6 w-6" />
                      <span className="text-xs font-medium">Changer l'image</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleCarouselUpload(idx, e)}
                      className="hidden"
                    />
                  </label>
                </div>
                
                {settings.carousel_images?.[idx] && (
                  <button
                    type="button"
                    onClick={() => {
                      const newImages = [...settings.carousel_images];
                      newImages[idx] = '';
                      setSettings({ ...settings, carousel_images: newImages });
                    }}
                    className="w-full py-2 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    Réinitialiser
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-card p-6 rounded-xl border space-y-4 lg:col-span-2">
          <div className="flex items-center space-x-3 mb-4">
            <Bell className="h-6 w-6 text-yellow-500" />
            <h3 className="text-xl font-semibold">Configuration des Notifications</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Gérez l'expiration automatique des notifications pour les utilisateurs de la plateforme.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3 md:col-span-2 bg-secondary/30 p-4 rounded-lg">
              <input
                type="checkbox"
                id="notification_expiry_enabled"
                checked={settings.notification_expiry_enabled}
                onChange={(e) => setSettings({ ...settings, notification_expiry_enabled: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <label htmlFor="notification_expiry_enabled" className="text-sm font-medium cursor-pointer">
                Activer la suppression automatique des notifications
              </label>
            </div>

            {settings.notification_expiry_enabled && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Expiration après (jours)</label>
                <input
                  type="number"
                  min="1"
                  value={settings.notification_expiry_days}
                  onChange={(e) => setSettings({ ...settings, notification_expiry_days: parseInt(e.target.value) || 1 })}
                  className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  placeholder="Ex: 5"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Les notifications antérieures à ce nombre de jours seront supprimées automatiquement chez les utilisateurs.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Admin Trials */}
        <div className="bg-card p-6 rounded-xl border space-y-4">
          <h3 className="text-lg font-semibold flex items-center space-x-2">
            <DollarSign className="h-5 w-5 text-primary" />
            <span>Essais administrateurs</span>
          </h3>
          <div className="space-y-2">
            <label className="text-sm font-medium">Essais attribués à la création d'un admin</label>
            <input
              type="number"
              min="0"
              value={settings.default_admin_trials}
              onChange={(e) => setSettings({ ...settings, default_admin_trials: parseInt(e.target.value) || 0 })}
              className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              placeholder="Ex: 5"
            />
            <p className="text-xs text-muted-foreground">
              Nombre d'essais IA automatiquement attribués lorsqu'un utilisateur est promu administrateur.
            </p>
          </div>
        </div>

        {/* Voice Intro Settings */}
        <div className="bg-card p-6 rounded-xl border space-y-4 lg:col-span-2">
          <h3 className="text-lg font-semibold flex items-center space-x-2">
            <Mic className="h-5 w-5 text-orange-500" />
            <span>Expression Orale — Voix d'introduction</span>
          </h3>
          <p className="text-xs text-muted-foreground">
            La voix d'intro présente les sujets avant chaque tâche via l'API OpenAI TTS. Elle consomme vos tokens API.
          </p>

          {/* Toggle: voice_intro_enabled */}
          <div className="flex items-center justify-between p-4 bg-background rounded-lg border">
            <div>
              <p className="font-medium text-sm">Activer la voix d'introduction</p>
              <p className="text-xs text-muted-foreground mt-0.5">Si désactivé, l'exercice démarre silencieusement (économise les tokens OpenAI).</p>
            </div>
            <button
              type="button"
              onClick={() => setSettings(s => ({ ...s, voice_intro_enabled: !s.voice_intro_enabled }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.voice_intro_enabled ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${settings.voice_intro_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Toggle: voice_intro_uses_credits — only shown when voice is enabled */}
          {settings.voice_intro_enabled && (
            <div className="flex items-center justify-between p-4 bg-background rounded-lg border">
              <div>
                <p className="font-medium text-sm">Débiter les crédits IA de l'utilisateur</p>
                <p className="text-xs text-muted-foreground mt-0.5">Si activé, chaque appel TTS déduit 1 crédit IA du compte de l'utilisateur.</p>
              </div>
              <button
                type="button"
                onClick={() => setSettings(s => ({ ...s, voice_intro_uses_credits: !s.voice_intro_uses_credits }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.voice_intro_uses_credits ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${settings.voice_intro_uses_credits ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          )}
        </div>

        {/* Multi-device login restriction */}
        <div className="bg-card p-6 rounded-xl border space-y-4 lg:col-span-2">
          <h3 className="text-lg font-semibold flex items-center space-x-2">
            <Key className="h-5 w-5 text-red-500" />
            <span>Sécurité — Connexion multi-appareils</span>
          </h3>
          <div className="flex items-center justify-between p-4 bg-background rounded-lg border">
            <div className="pr-4">
              <p className="font-medium text-sm">Bloquer la double connexion des utilisateurs</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Si activé, un utilisateur qui se connecte alors qu'une session est déjà active (autre appareil) voit son compte archivé automatiquement.
                Si désactivé, la connexion simultanée sur plusieurs appareils est autorisée.
              </p>
            </div>
            <button
              type="button"
              data-testid="multi-device-toggle"
              onClick={() => setSettings(s => ({ ...s, multi_device_restriction: !s.multi_device_restriction }))}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${settings.multi_device_restriction ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${settings.multi_device_restriction ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        {/* Save Button */}
        <div className="lg:col-span-2">
          <button
            type="submit"
            data-testid="save-settings-button"
            className="w-full flex items-center justify-center space-x-2 px-6 py-4 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-semibold"
          >
            <Save className="h-5 w-5" />
            <span>Enregistrer les paramètres</span>
          </button>
        </div>
      </form>

      {/* SMTP Configuration — separate save */}
      <div className="bg-card p-6 rounded-xl border space-y-6">
        <div className="flex items-center space-x-3">
          <Mail className="h-6 w-6 text-blue-500" />
          <div>
            <h3 className="text-xl font-semibold">Configuration SMTP</h3>
            <p className="text-sm text-muted-foreground">Paramètres d'envoi d'emails (mot de passe oublié, notifications)</p>
          </div>
        </div>

        {/* Provider toggle */}
        <div className="flex items-center space-x-2 p-1 bg-secondary/50 rounded-lg w-fit">
          <button
            type="button"
            onClick={() => setSmtpSettings(s => ({ ...s, smtp_provider: 'lws' }))}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${smtpSettings.smtp_provider === 'lws' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
          >
            LWS SMTP
          </button>
          <button
            type="button"
            onClick={() => setSmtpSettings(s => ({ ...s, smtp_provider: 'gmail' }))}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${smtpSettings.smtp_provider === 'gmail' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
          >
            Gmail
          </button>
        </div>

        {smtpSettings.smtp_provider === 'lws' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Hôte SMTP</label>
              <input
                type="text"
                value={smtpSettings.smtp_lws_host}
                onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_lws_host: e.target.value }))}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="mail.votre-domaine.fr"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Port</label>
              <select
                value={smtpSettings.smtp_lws_port}
                onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_lws_port: parseInt(e.target.value) }))}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <option value={587}>587 (STARTTLS — recommandé)</option>
                <option value={465}>465 (SSL/TLS)</option>
                <option value={25}>25 (non chiffré)</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Utilisateur SMTP</label>
              <input
                type="text"
                value={smtpSettings.smtp_lws_user}
                onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_lws_user: e.target.value }))}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="noreply@votre-domaine.fr"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center space-x-2">
                <Key className="h-4 w-4" />
                <span>Mot de passe SMTP</span>
              </label>
              <input
                type="password"
                value={smtpSettings.smtp_lws_password}
                onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_lws_password: e.target.value }))}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder={MASKED}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email expéditeur</label>
              <input
                type="email"
                value={smtpSettings.smtp_lws_from_email}
                onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_lws_from_email: e.target.value }))}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="noreply@votre-domaine.fr"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Nom expéditeur</label>
              <input
                type="text"
                value={smtpSettings.smtp_lws_from_name}
                onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_lws_from_name: e.target.value }))}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="TCF Canada"
                autoComplete="off"
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Adresse Gmail</label>
              <input
                type="email"
                value={smtpSettings.smtp_gmail_user}
                onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_gmail_user: e.target.value }))}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="votre.adresse@gmail.com"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">Sera aussi utilisée comme adresse expéditeur.</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center space-x-2">
                <Key className="h-4 w-4" />
                <span>Mot de passe d'application</span>
              </label>
              <input
                type="password"
                value={smtpSettings.smtp_gmail_password}
                onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_gmail_password: e.target.value }))}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder={MASKED}
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground">Générez-le dans <strong>Mon compte Google → Sécurité → Mots de passe des applications</strong>.</p>
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium">Nom expéditeur</label>
              <input
                type="text"
                value={smtpSettings.smtp_gmail_from_name}
                onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_gmail_from_name: e.target.value }))}
                className="w-full h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="TCF Canada"
                autoComplete="off"
              />
            </div>
          </div>
        )}

        {/* Save SMTP */}
        <button
          type="button"
          onClick={handleSmtpSave}
          disabled={smtpLoading}
          className="flex items-center space-x-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-semibold disabled:opacity-60"
        >
          {smtpLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          <span>{smtpLoading ? 'Sauvegarde...' : 'Sauvegarder SMTP'}</span>
        </button>

        {/* Test section */}
        <div className="border-t pt-5 space-y-3">
          <p className="text-sm font-medium flex items-center space-x-2">
            <FlaskConical className="h-4 w-4 text-orange-500" />
            <span>Tester la configuration</span>
          </p>
          <div className="flex gap-3">
            <input
              type="email"
              value={smtpTestEmail}
              onChange={(e) => setSmtpTestEmail(e.target.value)}
              className="flex-1 h-12 px-4 rounded-lg border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              placeholder="email-de-test@exemple.com"
            />
            <button
              type="button"
              onClick={handleSmtpTest}
              disabled={smtpTestLoading}
              className="flex items-center space-x-2 px-5 py-3 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors font-medium disabled:opacity-60 whitespace-nowrap"
            >
              {smtpTestLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              <span>{smtpTestLoading ? 'Envoi...' : 'Envoyer test'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
